import React from "react";

import { ProblemDetailError } from "@wingspanhq/wingspan-api-sdk";

import { IS_DEV_ENV } from "../../shared/constants/environment";
import { getApiErrorMessage, isNetworkError, isApiError } from "./apiError";
import {
  ErrorContext,
  ErrorMapEntry,
  ErrorMessageRule,
  errorMap,
  genericErrorMessage,
  networkErrorMessage
} from "./errorMap";

const detailCodeReason = (detailCode: string | undefined): string | undefined =>
  detailCode?.split(".").pop();

const getRuleMessage = (
  rules: ErrorMessageRule[],
  originalMessage: string
): React.ReactNode => {
  for (const rule of rules) {
    if (new RegExp(rule.pattern).test(originalMessage)) {
      return typeof rule.message === "string"
        ? originalMessage.replace(new RegExp(rule.pattern), rule.message)
        : rule.message;
    }
  }
  return undefined;
};

const getCodeMessage = (
  entry: ErrorMapEntry,
  code: string | undefined,
  detailCode: string | undefined
): React.ReactNode => {
  if (code === undefined) {
    return undefined;
  }
  const codeEntry = entry.byCode?.[code];
  if (codeEntry === undefined || React.isValidElement(codeEntry)) {
    return codeEntry;
  }
  if (typeof codeEntry === "string") {
    return codeEntry;
  }
  const byDetailCode = codeEntry as { [key: string]: React.ReactNode };
  if (detailCode !== undefined && byDetailCode[detailCode] !== undefined) {
    return byDetailCode[detailCode];
  }
  const reason = detailCodeReason(detailCode);
  if (reason !== undefined && byDetailCode[reason] !== undefined) {
    return byDetailCode[reason];
  }
  return byDetailCode["Other"];
};

const getRawErrorMessage = (
  rawMessage: string | undefined,
  forceShowRawError: boolean | undefined
): React.ReactNode => {
  if (IS_DEV_ENV) {
    return `"${rawMessage}"`;
  }
  if (forceShowRawError) {
    return rawMessage;
  }
  console.error(`WS ERROR "${rawMessage}"`);
  return undefined;
};

type ResolveErrorMessageParams = {
  error: unknown;
  context: ErrorContext;
  forceShowRawError?: boolean;
};

export const resolveErrorMessage = (
  params: ResolveErrorMessageParams
): React.ReactNode => {
  if (typeof params.error === "string") {
    return params.error;
  }

  const entry: ErrorMapEntry = errorMap[params.context];
  let message: React.ReactNode;

  if (isApiError(params.error)) {
    const errorMessage = getApiErrorMessage(params.error);

    if (errorMessage !== undefined && entry.byRegex !== undefined) {
      message = getRuleMessage(entry.byRegex, errorMessage);
    }
    if (message === undefined && params.error instanceof ProblemDetailError) {
      message = getCodeMessage(
        entry,
        params.error.code,
        params.error.detailCode
      );
    }
    message = message ?? entry.default;
    message =
      message ?? getRawErrorMessage(errorMessage, params.forceShowRawError);
  } else if (isNetworkError(params.error)) {
    message = networkErrorMessage;
  } else if (params.error instanceof Error) {
    if (entry.byRegex !== undefined) {
      message = getRuleMessage(entry.byRegex, params.error.message);
    }
    message = message ?? entry.default;
    message =
      message ??
      getRawErrorMessage(params.error.message, params.forceShowRawError);
  }

  return message ?? genericErrorMessage;
};
